/**
 * Authentication Service
 *
 * Handles all authentication operations with Supabase Auth.
 */

import { supabase } from '@/lib/supabase';
import type {
  LoginCredentials,
  RegisterCredentials,
  OAuthProvider,
  User,
  ProfileRow,
} from './types';
import { toUser } from './types';

const PROFILES_TABLE = 'pm_profiles';

async function fetchProfile(userId: string): Promise<ProfileRow | null> {
  const { data, error } = await supabase
    .from(PROFILES_TABLE)
    .select('*')
    .eq('id', userId)
    .maybeSingle();
  if (error) {
    console.error('Failed to fetch profile:', error);
    return null;
  }
  return data as ProfileRow | null;
}

/**
 * Login with email and password
 */
export async function loginWithPassword(credentials: LoginCredentials): Promise<User | null> {
  const { data, error } = await supabase.auth.signInWithPassword({
    email: credentials.email,
    password: credentials.password,
  });
  if (error) throw error;

  const profile = data.user ? await fetchProfile(data.user.id) : null;
  return toUser(data.user, profile);
}

/**
 * Register a new user. Returns whether a confirmation email still needs to
 * be clicked (Supabase won't issue a session until then).
 */
export async function registerUser(
  credentials: RegisterCredentials
): Promise<{ user: User | null; needsEmailConfirmation: boolean }> {
  const { data, error } = await supabase.auth.signUp({
    email: credentials.email,
    password: credentials.password,
    options: {
      data: {
        first_name: credentials.firstName,
        last_name: credentials.lastName,
        requires_legal_acceptance: true,
      },
      emailRedirectTo: `${window.location.origin}/auth/confirm`,
    },
  });
  if (error) throw error;

  const profile = data.user ? await fetchProfile(data.user.id) : null;
  return { user: toUser(data.user, profile), needsEmailConfirmation: !data.session };
}

/**
 * Login with OAuth provider
 */
export async function loginWithOAuth(provider: OAuthProvider): Promise<User | null> {
  const { error } = await supabase.auth.signInWithOAuth({
    provider,
    options: { redirectTo: `${window.location.origin}/auth/confirm` },
  });
  if (error) throw error;
  // OAuth flow redirects; user is null until callback completes
  return null;
}

/**
 * Logout current user
 */
export async function logout(): Promise<void> {
  await supabase.auth.signOut();
}

/**
 * Refresh authentication
 */
export async function refreshAuth(): Promise<User | null> {
  const { data: sessionData } = await supabase.auth.getSession();
  if (!sessionData.session) return null;

  const { data, error } = await supabase.auth.getUser();
  if (error || !data.user) return null;
  const profile = await fetchProfile(data.user.id);
  return toUser(data.user, profile);
}

/**
 * Request password reset email
 */
export async function requestPasswordReset(email: string): Promise<void> {
  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}/reset-password`,
  });
  if (error) throw error;
}

/**
 * Update password (used inside the reset-password flow once user is in session)
 */
export async function updatePassword(newPassword: string): Promise<void> {
  const { error } = await supabase.auth.updateUser({ password: newPassword });
  if (error) throw error;
}

/**
 * Confirm password reset (kept for API parity; calls updatePassword)
 */
export async function confirmPasswordReset(
  _token: string,
  password: string,
  passwordConfirm: string
): Promise<void> {
  if (password !== passwordConfirm) throw new Error('Passwords do not match');
  await updatePassword(password);
}

/**
 * Request email verification (Supabase auto-sends; this resends)
 */
export async function requestEmailVerification(email: string): Promise<void> {
  const { error } = await supabase.auth.resend({ type: 'signup', email });
  if (error) throw error;
}

/**
 * Confirm email verification (handled automatically by Supabase via redirect URL)
 */
export async function confirmEmailVerification(_token: string): Promise<void> {
  // Supabase handles email verification through the redirect link automatically.
}

/**
 * Check if user is authenticated (synchronous best-effort)
 */
export async function isAuthenticated(): Promise<boolean> {
  const { data } = await supabase.auth.getSession();
  return !!data.session;
}

/**
 * Get current user (with profile)
 */
export async function getCurrentUser(): Promise<User | null> {
  const { data } = await supabase.auth.getUser();
  if (!data.user) return null;
  const profile = await fetchProfile(data.user.id);
  return toUser(data.user, profile);
}

/**
 * Get auth token
 */
export async function getAuthToken(): Promise<string | null> {
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token ?? null;
}

/**
 * Complete onboarding for the current session (first/last name + email).
 * Used by the /onboarding page after email confirmation or Google sign-in.
 */
export async function completeOnboarding(input: {
  firstName: string;
  lastName: string;
  email: string;
  legalAcceptedAt?: string;
}): Promise<User | null> {
  const { data: userData, error: userError } = await supabase.auth.getUser();
  if (userError || !userData.user) throw new Error('Must be logged in');

  const profile = {
    id: userData.user.id,
    first_name: input.firstName,
    last_name: input.lastName,
    email: input.email,
    ...(input.legalAcceptedAt ? { legal_accepted_at: input.legalAcceptedAt } : {}),
  };

  const { data, error } = await supabase.from(PROFILES_TABLE).upsert(profile).select().single();
  if (error) throw error;

  return toUser(userData.user, data as ProfileRow);
}
